import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { AssetResponse, useGetAllAsset } from '@ping/api';
import { t } from '@ping/helpers';
import { CoinItem, SearchField } from '@ping/uikit';
import { Select } from '@ping/uikit/SelectA11Y';
import { alwaysTrue, DateTime } from '@ping/utils';

import style from './style.module.scss';

import type { PopoverPlacement } from '../PrimitivePopover/PrimitivePopoverRoot';

interface IAssetSelectProps extends ICustomizable {
  assetId?: string;
  label?: string;
  name?: string;
  isDisabled?: boolean;
  magnet?: IMagnet;
  boundaryRef?: React.MutableRefObject<HTMLElement>;
  onChange?(assetId: string, asset: AssetResponse): void;
  onOpenChange?: (isOpen: boolean) => void;
  onPanelPlacementChange?: (placement: PopoverPlacement) => void;
  /**
   * A function that is used to filter the asset options.
   */
  filterFn?: (asset: AssetResponse) => boolean;
}

export const AssetSelect = (props: IAssetSelectProps) => {
  const [items, setItems] = useState<AssetResponse[]>([]);

  const { data: assets, isLoading: isAssetsLoading } = useGetAllAsset({
    query: {
      staleTime: DateTime.days(1),
      select: ({ data: assets }) => {
        const list = assets.filter(props.filterFn ?? alwaysTrue) || [];
        const map = assets.reduce((map, asset) => map.set(asset.id, asset), new Map<string, AssetResponse>());
        return { list, map };
      },
    },
  });

  useEffect(() => filterViewItems(''), [isAssetsLoading]);

  const filterViewItems = (value: string) => {
    if (isAssetsLoading || !assets) {
      return;
    }

    const text = value.trim().toLowerCase();
    const items = assets.list.filter(
      asset => asset.asset_name.toLowerCase().includes(text) || asset.id.toLowerCase().includes(text)
    );

    setItems(items);
  };

  const handleOnSelectionChange = (assetId: string) => {
    filterViewItems('');
    props.onChange?.(assetId, assets.map.get(assetId));
  };

  return (
    <Select
      className={clsx(style['asset-select'], props.className)}
      items={items}
      isLoading={isAssetsLoading}
      isDisabled={props.isDisabled}
      magnet={props.magnet}
      label={props.label}
      name={props.name}
      boundaryRef={props.boundaryRef}
      defaultSelectedKey={props.assetId}
      onSelectionChange={handleOnSelectionChange}
      onOpenChange={isOpen => {
        props.onOpenChange?.(isOpen);
        !isOpen && filterViewItems('');
      }}
      onPanelPlacementChange={props.onPanelPlacementChange}
      emptyState={t('No available asset')}
      buttonContent={
        props.assetId ? (
          <CoinItem
            className={style['asset-select__selection']}
            coinName={props.assetId?.toUpperCase()}
            iconUrl={assets?.map.get(props.assetId)?.icon_url}
          />
        ) : (
          t('Select')
        )
      }
      topElement={
        <>
          <SearchField
            className={style['asset-select__search']}
            placeholder={t('Search')}
            label={t('Search through assets')}
            onChange={filterViewItems}
            disallowedKeysList={[' ', 'Space']}
            isDisabled={isAssetsLoading}
          />
          {!items.length && !isAssetsLoading && <p className={style['asset-select__not-found']}>{t('Not Found')}</p>}
        </>
      }
    >
      {asset => (
        <Select.Item
          key={asset.id}
          textValue={asset.asset_name.toUpperCase()}
          aria-label={asset.asset_name.toUpperCase()}
        >
          <CoinItem
            className='cursor-pointer '
            coinName={asset.id.toUpperCase()}
            iconUrl={asset.icon_url}
            title={`${asset.asset_name} (${asset.id.toUpperCase()})`}
          />
        </Select.Item>
      )}
    </Select>
  );
};
